<template>
	<div class="container-fluid p-4">
		<div class="d-flex justify-content-between align-items-center mb-4">
			<div>
				<h1 class="mb-0">Pesticide Catalog</h1>
				<p class="text-muted mb-0">
					The shared reference catalog of pesticide products, curated by
					Gleipnir for all Nidus districts. Add an entry to your district's
					list, or remove one you already hold.
				</p>
			</div>
			<RouterLink class="btn btn-outline-secondary" to="/_/inventory/products">
				<i class="bi bi-arrow-left me-1"></i>Back to products
			</RouterLink>
		</div>

		<div class="card">
			<div class="card-body">
				<ProductTable
					:rows="rows"
					variant="catalog"
					:loading="loading"
					:error="error"
					:pending-key="pendingKey"
					empty-text="No pesticide products in the catalog."
					@add="addToDistrict"
					@remove="removeFromDistrict"
				/>
			</div>
		</div>

		<ToastNotification
			:title="toastTitle"
			:message="toastMessage"
			:show="toastShow"
			@update:show="toastShow = $event"
		/>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";

import { apiClient } from "@/client";
import ProductTable from "@/components/pesticide/ProductTable.vue";
import { buildCatalogRow } from "@/components/pesticide/productTable";
import ToastNotification from "@/components/ToastNotification.vue";
import { useStoreProduct } from "@/store/product";
import type { PesticideCatalogProduct } from "@/type/api";

const storeProduct = useStoreProduct();
const products = ref<PesticideCatalogProduct[]>([]);
const loading = ref(false);
const error = ref("");
// The catalog entry whose add/remove is in flight, so only its row shows a
// spinner. The entry id is stable across both actions.
const pendingKey = ref<number | null>(null);

const toastTitle = ref("");
const toastMessage = ref("");
const toastShow = ref(false);

function showNotification(title: string, message: string) {
	toastTitle.value = title;
	toastMessage.value = message;
	toastShow.value = true;
}

const rows = computed(() => products.value.map(buildCatalogRow));

async function load(silent = false) {
	if (!silent) loading.value = true;
	error.value = "";
	try {
		const data = await apiClient.JSONGet<{
			products: PesticideCatalogProduct[];
			total: number;
		}>("/api/pesticide-catalog");
		products.value = data.products ?? [];
	} catch (e: any) {
		error.value = e?.message || "Failed to load pesticide products.";
	} finally {
		if (!silent) loading.value = false;
	}
}

// Adding goes through the district-product create path the server already owns:
// the new row is seeded from this catalog entry, and the reload reads back the
// server-computed in-list state rather than assuming it.
async function addToDistrict(catalogProductID: string, key: number) {
	pendingKey.value = key;
	try {
		const created = await storeProduct.createFromCatalog(catalogProductID);
		await load(true);
		showNotification(
			"Added to your district list",
			`"${created.name}" is now in your district's list.`,
		);
	} catch (e: any) {
		showNotification(
			"Failed to add product",
			e?.message || "Please try again.",
		);
	} finally {
		pendingKey.value = null;
	}
}

// Removing retires the district's own product; the shared catalog row is never
// touched, and the reload reports the entry as not held.
async function removeFromDistrict(districtProductID: number, key: number) {
	const name =
		products.value.find(
			(product) => product.district_product_id === districtProductID,
		)?.name ?? "The product";
	pendingKey.value = key;
	try {
		await storeProduct.retire(districtProductID);
		await load(true);
		showNotification(
			"Removed from your district list",
			`"${name}" is no longer in your district's list.`,
		);
	} catch (e: any) {
		showNotification(
			"Failed to remove product",
			e?.message || "Please try again.",
		);
	} finally {
		pendingKey.value = null;
	}
}

onMounted(load);
</script>
